import { autoinject, transient } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { ValidationControllerFactory, ValidationController, ValidationRules, validateTrigger } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { GhgService } from "../../../../../../services/ghg/ghg.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { EmailRecipientTypeEnum, SubtaskCorrespondenceEmailModel } from "../../../../../../models/vetting/vetting.models";
import { BindingEngine } from "aurelia-binding";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { EmailValidator } from "../../../../../../utilities/email-validator";

@autoinject
@transient()
export class GenerateExternalGHGVerificationDialog {

    public alreadyExists: boolean;
    public vesselId: number;
    public vesselName: string;
    public imo: string;
    public correspondenceMessage: string = "";
    public ghgVerificationSubmitted: boolean = false;
    public validator: ValidationController;
    emails: {
        email: string
    }[] = [];

    verifyOptions: { id: number, name: string, isSelected: boolean, show: boolean }[] = [
        {
            id: 0,
            name: 'Vessel Details / EEDI / EVDI',
            isSelected: false,
            show: true
        },
        {
            id: 1,
            name: 'Energy Efficiency Existing Ship Index (EEXI)',
            isSelected: false,
            show: true
        },
        {
            id: 2,
            name: 'Energy Saving Equipment',
            isSelected: false,
            show: true
        },
        {
            id: 3,
            name: 'Carbon Intensity Indicator (CII)',
            isSelected: false,
            show: true
        }
    ];

    constructor(private ghgService: GhgService,
        private dialogClientService: DialogClientService,
        private userClaimsService: UserClaimsService,
        private notificationService: NotificationClientService,
        validationControllerFactory: ValidationControllerFactory,
        bindingEngine: BindingEngine) {
        this.validator = validationControllerFactory.createForCurrentScope();
        this.validator.addRenderer(new CustomValidationRenderer());
        this.validator.changeTrigger(validateTrigger.manual);
        this.verifyOptions.forEach(verifyOption => {
            bindingEngine.expressionObserver(verifyOption, 'isSelected')
                .subscribe(this.verifyOptionsChanged.bind(this))
        });
        this.verifyOptions[1].show = this.userClaimsService.hasPermission(Claims.CanAddEexiVerification);
    }

    onVerificationOptionToggled(verifyOption) {
        const evdiOption = this.verifyOptions[0];
        const eexiOption = this.verifyOptions[1];

        if (evdiOption.isSelected && eexiOption.isSelected) {
            if (verifyOption.id === evdiOption.id) eexiOption.isSelected = false;
            else evdiOption.isSelected = false;
        }
    }

    verifyOptionsChanged() {
        this.updateVerificationCheckCorrespondenceMessage();
    }

    public async activate(params: any): Promise<void> {
        this.vesselId = params.vesselId;
        this.vesselName = params.vesselName;
        this.imo = params.imo;
        this.updateVerificationCheckCorrespondenceMessage();
        const response = await this.ghgService.getVesselPendingGhgVerification(this.vesselId);
        this.alreadyExists = response.ghgVerificationId != null;
    }

    updateVerificationCheckCorrespondenceMessage() {
        this.correspondenceMessage = `Good day,
            <br/>${this.generateVerificationCheckCorrespondenceMessage(this.verifyOptions)}
            <br/>Please click the link below to complete the environmental rating verification.
            <br/>Many Thanks.
            <br/>`;
    }

    generateVerificationCheckCorrespondenceMessage(verifyOptions) {
        // If no verification options have been selected
        if (!verifyOptions.filter(option => option.isSelected).length) {
            return ""
        }
        let message = "The following environmental ratings of your vessel requires updating.<ol>";
        verifyOptions.forEach(option => {
            if (option.isSelected) {
                message += `<li class="margin-left;">${option.name}</li>`
            }
        })
        message += "</ol>"
        return message;
    }

    attached() {
        this.setRules();
        this.addEmail();
    }

    protected close() {
        this.dialogClientService.hide();
    }

    protected saveItem(): Promise<void> {

        return new Promise((resolve, reject) => {
            this.validator.validate().then(r => {
                if (r.valid) {

                    let correspondenceEmail = '';

                    let additionalEmailsToNotify = [];

                    if (this.emails && this.emails.filter(x => x.email != null && x.email.trim() != '').length > 0) {
                        correspondenceEmail = this.emails[0].email;
                        const validEmails = this.emails.filter(x => x.email != null && x.email.trim() != '')
                            .slice(1) //filter out first email which would be set in the correspondence email
                            .map(x => {
                                return <SubtaskCorrespondenceEmailModel>{
                                    email: x.email,
                                    emailRecipientTypeId: EmailRecipientTypeEnum.To
                                };
                            });
                        if (validEmails.length > 0) {
                            additionalEmailsToNotify = validEmails;
                        }
                    }
                    this.ghgService.generateExternalGhgVerification({
                        vesselId: this.vesselId,
                        correspondenceMessage: this.correspondenceMessage,
                        correspondenceEmail: {
                            email: correspondenceEmail,
                            emailRecipientTypeId: EmailRecipientTypeEnum.To
                        },
                        additionalEmailsToNotify: additionalEmailsToNotify,
                        verifyGhg: this.verifyOptions.find(x => x.id === 0).isSelected,
                        verifyEexi: this.verifyOptions.find(x => x.id === 1).isSelected,
                        verifyEnergySavingEquipment: this.verifyOptions.find(x => x.id === 2).isSelected,
                        verifyCii: this.verifyOptions.find(x => x.id === 3).isSelected
                    }).then(response => {
                        const ghgAlreadyExists = response.ghgAlreadyExists;
                        if (ghgAlreadyExists) {
                            this.notificationService.show("Validation Error", "Environmental Review Request for this vessel already exists in task backlog.", NotificationType.Error);
                        }
                        resolve();
                        this.ghgVerificationSubmitted = true;
                    }).catch(() => {
                        reject();
                    });
                }
                else {
                    reject();
                }
            });
        })
    }

    setRules() {
        ValidationRules
            .ensure<GenerateExternalGHGVerificationDialog, any>(x => x.emails)
            .required()
            .withMessage("At least 1 email required")
            .minItems(1)
            .withMessage("At least 1 email required")
            .ensure<any>(x => x.verifyOptions)
            .satisfies((x => x.some((input) => input.isSelected)))
            .withMessage("Select at least 1 verify option")
            .on(this);

        this.validator.addObject(this);
    }

    removeEmail(index) {
        const deleted = this.emails.splice(index, 1);
        this.validator.removeObject(deleted[0]);
    }

    getEmailRules(email: { email: string }) {

        const rules = ValidationRules.ensure((x: { email: string }) => x.email)
            .satisfies(x => {
                return !(((x == null || x.trim() == '')));
            })
            .withMessage("Email Required")
            .satisfies(x => EmailValidator.isValidEmail(x))
            .on(email).rules;

        return rules;
    }

    addEmail() {
        const email = {
            email: ''
        };
        this.emails.push(email);

        const rules = this.getEmailRules(email);
        this.validator.addObject(email, rules);
    }
}